import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Form, Input, Modal, Select } from "antd";
import {
  searchUserAccountPersonOptions,
  type AccountCommandError,
} from "./userAccountAdminApi";

export type CreateUserAccountValues = Readonly<{
  personId?: string | undefined;
  displayName?: string | undefined;
  username: string;
  homeOrganizationId: string;
}>;

type CreateFormValues = {
  displayName?: string | undefined;
  personId?: string | undefined;
  username?: string | undefined;
  homeOrganizationId?: string | undefined;
};

const PERSON_SEARCH_DEBOUNCE_MS = 300;
const PERSON_SEARCH_SIZE = 20;

function isAbortError(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { name?: string }).name === "AbortError";
}

function describeCreateError(code: string): string {
  switch (code) {
    case "PERSON_USER_ACCOUNT_CONFLICT":
      return "Người này đã có tài khoản";
    case "USER_ACCOUNT_UNIQUENESS_CONFLICT":
      return "Tên đăng nhập hoặc thông tin đã trùng với tài khoản hiện có";
    case "USER_ACCOUNT_INVALID_REFERENCE":
      return "Thông tin được chọn không hợp lệ, hãy chọn người dùng khác";
    default:
      return "Không thể tạo tài khoản";
  }
}

export function CreateUserAccountModal({
  open,
  organizationOptions,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  readonly open: boolean;
  readonly organizationOptions: readonly { value: string; label: string }[];
  readonly submitting: boolean;
  readonly error: AccountCommandError | null;
  readonly onCancel: () => void;
  readonly onSubmit: (values: CreateUserAccountValues) => void;
}) {
  const [form] = Form.useForm<CreateFormValues>();
  const [personOptions, setPersonOptions] = useState<readonly { value: string; label: string }[]>([]);
  const [personSearching, setPersonSearching] = useState(false);
  const searchControllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  const loadPersonOptions = useCallback(async (query: string) => {
    searchControllerRef.current?.abort();
    const controller = new AbortController();
    searchControllerRef.current = controller;
    setPersonSearching(true);
    try {
      const page = await searchUserAccountPersonOptions(query, 0, PERSON_SEARCH_SIZE, controller.signal);
      if (controller.signal.aborted) return;
      setPersonOptions(page.data.map((option) => ({ value: option.personId, label: option.displayName })));
    } catch (error) {
      if (isAbortError(error)) return;
      setPersonOptions([]);
    } finally {
      if (!controller.signal.aborted) setPersonSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    void loadPersonOptions("");
    return () => {
      searchControllerRef.current?.abort();
      window.clearTimeout(debounceRef.current);
    };
  }, [open, form, loadPersonOptions]);

  const handlePersonSearch = useCallback(
    (query: string) => {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        void loadPersonOptions(query);
      }, PERSON_SEARCH_DEBOUNCE_MS);
    },
    [loadPersonOptions],
  );

  const handleSubmit = async () => {
    let values: CreateFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const personId = values.personId && values.personId.trim() !== "" ? values.personId.trim() : undefined;
    const displayName = values.displayName && values.displayName.trim() !== "" ? values.displayName.trim() : undefined;

    if ((personId && displayName) || (!personId && !displayName)) {
      return;
    }

    if (personId) {
      onSubmit({
        personId,
        username: (values.username as string).trim(),
        homeOrganizationId: values.homeOrganizationId as string,
      });
    } else if (displayName) {
      onSubmit({
        displayName,
        username: (values.username as string).trim(),
        homeOrganizationId: values.homeOrganizationId as string,
      });
    }
  };

  return (
    <Modal
      open={open}
      title="Tạo tài khoản"
      okText="Tạo tài khoản"
      cancelText="Hủy"
      okButtonProps={{ disabled: submitting, loading: submitting }}
      cancelButtonProps={{ disabled: submitting }}
      mask={{ closable: false }}
      onCancel={onCancel}
      onOk={() => void handleSubmit()}
    >
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          title={describeCreateError(error.code)}
          description={
            <div>
              {error.code === "PERSON_USER_ACCOUNT_CONFLICT" && (
                <div>Hãy đóng biểu mẫu này và mở tài khoản hiện có trong danh sách</div>
              )}
              {error.requestId && <div>Mã hỗ trợ: {error.requestId}</div>}
            </div>
          }
        />
      )}
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="displayName"
          label="Họ và tên"
          dependencies={["personId"]}
          rules={[
            { max: 255, message: "Họ và tên tối đa 255 ký tự" },
            () => ({
              validator(_, value: unknown) {
                const personVal: unknown = form.getFieldValue("personId");
                const person = typeof personVal === "string" ? personVal.trim() : "";
                const name = typeof value === "string" ? value.trim() : "";
                if (!name && !person) {
                  return Promise.reject(new Error("Bắt buộc nhập họ và tên HOẶC chọn người dùng liên kết"));
                }
                if (name && person) {
                  return Promise.reject(new Error("Chỉ chọn người dùng HOẶC nhập họ và tên, không điền cả hai"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="Nhập họ và tên cán bộ (hoặc chọn hồ sơ liên kết bên dưới)" />
        </Form.Item>
        <Form.Item
          name="personId"
          label="Liên kết hồ sơ người"
          dependencies={["displayName"]}
          rules={[
            () => ({
              validator(_, value: unknown) {
                const nameVal: unknown = form.getFieldValue("displayName");
                const name = typeof nameVal === "string" ? nameVal.trim() : "";
                const person = typeof value === "string" ? value.trim() : "";
                if (!name && !person) {
                  return Promise.reject(new Error("Bắt buộc nhập họ và tên HOẶC chọn người dùng liên kết"));
                }
                if (name && person) {
                  return Promise.reject(new Error("Chỉ chọn người dùng HOẶC nhập họ và tên, không điền cả hai"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Select
            allowClear
            showSearch
            filterOption={false}
            loading={personSearching}
            options={[...personOptions]}
            placeholder="Chọn người dùng liên kết (nếu có)"
            notFoundContent={personSearching ? undefined : "Không tìm thấy người dùng"}
            virtual={false}
            onSearch={handlePersonSearch}
          />
        </Form.Item>
        <Form.Item
          name="username"
          label="Tên đăng nhập"
          rules={[
            { required: true, message: "Bắt buộc nhập tên đăng nhập" },
            { max: 100, message: "Tên đăng nhập tối đa 100 ký tự" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="homeOrganizationId"
          label="Đơn vị công tác"
          rules={[{ required: true, message: "Bắt buộc chọn đơn vị công tác" }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={[...organizationOptions]}
            placeholder="Chọn đơn vị công tác"
            virtual={false}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
